import { useState, useEffect, useMemo } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useQuery } from '@tanstack/react-query'

interface ReplyMetrics {
  totalReplies: number
  todayReplies: number
  monthToDateReplies: number
  positiveReplies: number
  autoReplies: number
  categoryBreakdown: Record<string, number>
  loading: boolean
  error: string | null
  refetch: () => void
}

interface UseReplyMetricsOptions {
  clientFilter?: string | null
  timeFrame?: string
  customDateRange?: { start: string; end: string } | null
}

export function useReplyMetrics(options: UseReplyMetricsOptions = {}) {
  const { clientFilter, timeFrame, customDateRange } = options

  // Memoize the query key to prevent unnecessary refetches
  const queryKey = useMemo(() => ['reply-metrics', clientFilter, timeFrame, customDateRange], [clientFilter, timeFrame, customDateRange])

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      // Calculate date range based on timeFrame or customDateRange
      let startDate: string | null = null
      let endDate: string | null = null

      if (customDateRange) {
        startDate = customDateRange.start
        endDate = customDateRange.end
      } else if (timeFrame) {
        const now = new Date()
        switch (timeFrame) {
          case 'today':
            startDate = now.toISOString().split('T')[0]
            endDate = startDate
            break
          case 'yesterday':
            const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
            startDate = yesterday.toISOString().split('T')[0]
            endDate = startDate
            break
          case 'last_7_days':
            const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
            startDate = sevenDaysAgo.toISOString().split('T')[0]
            endDate = now.toISOString().split('T')[0]
            break
          case 'last_30_days':
            const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            startDate = thirtyDaysAgo.toISOString().split('T')[0]
            endDate = now.toISOString().split('T')[0]
            break
          case 'this_month':
          case 'mtd':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
            endDate = now.toISOString().split('T')[0]
            break
        }
      }

      // Get today's date in UK timezone for today's replies calculation
      const ukDate = new Date().toLocaleDateString('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).split('/').reverse().join('-')

      const tomorrow = new Date(new Date(ukDate).getTime() + 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0]

      // Get month start date (first day of current month) in UK timezone
      const now = new Date()
      const ukNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/London' }))
      const monthStart = new Date(ukNow.getFullYear(), ukNow.getMonth(), 1)
        .toISOString().split('T')[0]

      // Helper function to apply filters to query
      const applyFilters = (query: any) => {
        let filteredQuery = query
        
        if (clientFilter && clientFilter !== 'all') {
          filteredQuery = filteredQuery.eq('client_id', clientFilter)
        }
        
        if (startDate && endDate) {
          if (startDate === endDate) {
            // Single day
            filteredQuery = filteredQuery
              .gte('created_at', startDate)
              .lt('created_at', new Date(new Date(startDate).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0])
          } else {
            // Date range
            filteredQuery = filteredQuery
              .gte('created_at', startDate)
              .lte('created_at', endDate + 'T23:59:59.999Z')
          }
        }
        
        return filteredQuery
      }

      // ROLLBACK: Use obsync_dynamic_reports table only
      // Parallel queries for better performance
      const [totalRepliesResult, todayRepliesResult, monthToDateRepliesResult, positiveRepliesResult, autoRepliesResult] = await Promise.all([
        // Get total replies
        applyFilters(
          supabase
            .from('obsync_dynamic_reports')
            .select('*', { count: 'exact', head: true })
            .eq('event_type', 'EMAIL_REPLY')
        ),
        // Get today's replies (UK timezone) - always calculated regardless of filters
        supabase
          .from('obsync_dynamic_reports')
          .select('*', { count: 'exact', head: true })
          .eq('event_type', 'EMAIL_REPLY')
          .gte('created_at', ukDate)
          .lt('created_at', tomorrow),
        // Get month-to-date replies (from first day of current month to now) - always calculated regardless of filters
        supabase
          .from('obsync_dynamic_reports')
          .select('*', { count: 'exact', head: true })
          .eq('event_type', 'EMAIL_REPLY')
          .gte('created_at', monthStart)
          .lt('created_at', tomorrow),
        // Get positive replies
        applyFilters(
          supabase
            .from('obsync_dynamic_reports')
            .select('*', { count: 'exact', head: true })
            .eq('event_type', 'EMAIL_REPLY')
            .eq('response_category', 'Interested')
        ),
        // Get auto replies (auto-reply or OOO)
        applyFilters(
          supabase
            .from('obsync_dynamic_reports')
            .select('*', { count: 'exact', head: true })
            .eq('event_type', 'EMAIL_REPLY')
            .in('response_category', ['Auto-Reply', 'Out Of Office'])
        )
      ])

      // Get category breakdown (only if needed)
      const categoryQuery = supabase
        .from('obsync_dynamic_reports')
        .select('response_category')
        .eq('event_type', 'EMAIL_REPLY')
        .not('response_category', 'is', null)
      
      const { data: categoryData } = await applyFilters(categoryQuery)

      const categoryBreakdown: Record<string, number> = {}
      categoryData?.forEach(item => {
        const category = item.response_category || 'Unknown'
        categoryBreakdown[category] = (categoryBreakdown[category] || 0) + 1
      })

      return {
        totalReplies: totalRepliesResult.count || 0,
        todayReplies: todayRepliesResult.count || 0,
        monthToDateReplies: monthToDateRepliesResult.count || 0,
        positiveReplies: positiveRepliesResult.count || 0,
        autoReplies: autoRepliesResult.count || 0,
        categoryBreakdown
      }
    },
    staleTime: 30 * 1000, // 30 seconds - more aggressive to ensure fresh data
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to page
    refetchOnMount: true, // Always refetch when component mounts
    placeholderData: (previousData) => previousData,
  })

  return {
    totalReplies: data?.totalReplies || 0,
    todayReplies: data?.todayReplies || 0,
    monthToDateReplies: data?.monthToDateReplies || 0,
    positiveReplies: data?.positiveReplies || 0,
    autoReplies: data?.autoReplies || 0,
    categoryBreakdown: data?.categoryBreakdown || {},
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Unknown error') : null,
    refetch, // Expose refetch function so UI can force refresh
  }
}
